/**
 * MCP Servers — Default tab for Connections section
 *
 * Shows MCP servers grouped by 3 tiers:
 * - Tier 1: IQ Native (in-process, highest trust)
 * - Tier 2: Customer Connected (Docker sandboxed)
 * - Tier 3: Forge Generated (auto-built from API specs)
 *
 * Features:
 * - Search across name, namespace, endpoint URL
 * - Tier filter
 * - Card grid per tier section
 * - Sheet drill-in for tool catalog
 * - Test, reset circuit breaker, remove actions
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Search, Plug, Server, MoreHorizontal,
  RefreshCw, Eye, Settings2, RotateCcw, Trash2, Lock, Unlock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { StatusDot } from '@/components/shared/status-dot';
import { statusToDot, STATUS_BADGE_STYLES } from '@/components/connections/connection-status-badge';
import { CB_BADGE_STYLES } from '@/components/connections/circuit-breaker-badge';
import { TIER_BADGE_STYLES, TIER_ICONS } from '@/components/connections/tier-badge';
import { ConnectionSetupAffordance } from '@/components/connections/connection-setup-affordance';
import {
  FlavorBadge,
  serverFlavor,
  type ConnectionFlavor,
} from '@/components/connections/flavor-badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  TIER_LABELS,
  PERMISSION_LABELS,
  type McpServer,
  type McpTier,
} from '@/lib/mock-connections-data';
import {
  useMcpServers,
  useTestMcpServer,
  useResetMcpCircuitBreaker,
  useUninstallMcpServer,
} from '@/lib/use-connections-module';
import { useAuth } from '@/contexts/auth-context';
import { useHasPermission } from '@/lib/use-has-permission';
import { decideConnectionsRoleGate } from './-connections-role-gate.helpers';

export const Route = createFileRoute('/connections/')({
  component: McpServersPage,
});

// Status helpers, badge styles, tier icons imported from @/components/connections/

// ── Main Page ──────────────────────────────────────────────────────────────

// Exported so the nested-IA alias route at /connections/mcp (added
// round 4 / #228) can render the same MCP-server-list component the
// /connections/ index does.
export function McpServersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  // Real-data: tenant_mcp_installs joined with system_mcp_servers
  const { data: realServers = [] } = useMcpServers();
  const servers = realServers;
  // Bug #758: hide the Register Server CTA for member / viewer roles —
  // the route they'd land on (/connections/new-mcp) now denies them
  // anyway, but the inert button advertises capability the role does
  // not have. Sysadmin context keeps the button visible so the tester
  // can still reach the placeholder that redirects to /sysadmin/catalog.
  // Bug #758 / B1-T3: hide the CTA for tenants without write:connections.
  const { contextKind } = useAuth();
  const canWriteConnections = useHasPermission('write', 'connections');
  const canRegisterMcp =
    decideConnectionsRoleGate({ contextKind, canWriteConnections }) !==
    'deny_role_too_low';
  const [selectedTier, setSelectedTier] = useState<'all' | McpTier>('all');
  // bug #6 (UI-SPEC v2.1) — Flavor filter (All / Marketplace / External),
  // driven by the server's marketplace lineage (systemServerId).
  const [selectedFlavor, setSelectedFlavor] = useState<'all' | ConnectionFlavor>('all');
  const [sheetServer, setSheetServer] = useState<McpServer | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<McpServer | null>(null);

  // Filter servers
  const filtered = servers.filter(s => {
    if (selectedTier !== 'all' && s.tier !== selectedTier) return false;
    if (selectedFlavor !== 'all' && serverFlavor(s) !== selectedFlavor) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return s.displayName.toLowerCase().includes(q) ||
      s.namespace.toLowerCase().includes(q) ||
      s.endpointUrl.toLowerCase().includes(q);
  });

  // Group by tier
  const tiers: McpTier[] = ['tier1', 'tier2', 'tier3'];
  const grouped = tiers
    .filter(t => selectedTier === 'all' || selectedTier === t)
    .map(t => ({ tier: t, servers: filtered.filter(s => s.tier === t) }));

  // Real backend mutations.
  const testServer = useTestMcpServer();
  const resetCircuitBreaker = useResetMcpCircuitBreaker();
  const uninstallServer = useUninstallMcpServer();

  const handleTest = (id: string) => {
    const name = servers.find(s => s.id === id)?.displayName ?? 'server';
    const t = toast.loading(`Testing ${name}...`);
    testServer.mutate(id, {
      onSuccess: (res) => {
        toast.dismiss(t);
        if (res.ok) {
          toast.success(`${name}: ${res.message}`);
        } else if (res.status === 'unknown') {
          toast.message(`${name}`, { description: res.message });
        } else {
          toast.error(`${name}: ${res.message}`);
        }
      },
      onError: (err) => {
        toast.dismiss(t);
        toast.error(`${name}: ${err.message}`);
      },
    });
  };

  const handleResetCB = (id: string) => {
    const name = servers.find(s => s.id === id)?.displayName ?? 'server';
    resetCircuitBreaker.mutate(id, {
      onSuccess: () => toast.success(`Circuit breaker reset for ${name}`),
      onError: (err) => toast.error(`Reset failed: ${err.message}`),
    });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    uninstallServer.mutate(target.id, {
      onSuccess: () => {
        toast.success(`Uninstalled ${target.displayName}`);
        setDeleteTarget(null);
      },
      onError: (err) => toast.error(`Uninstall failed: ${err.message}`),
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Top bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search servers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={selectedTier} onValueChange={v => setSelectedTier(v as 'all' | McpTier)}>
          <SelectTrigger aria-label="Filter MCP connections by tier" className="w-48">
            <SelectValue placeholder="All Tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="tier1">Tier 1 — IQ Native</SelectItem>
            <SelectItem value="tier2">Tier 2 — Connected</SelectItem>
            <SelectItem value="tier3">Tier 3 — Forge</SelectItem>
          </SelectContent>
        </Select>
        {/* bug #6 — Flavor filter (UI-SPEC v2.1): Marketplace vs External. */}
        <Select value={selectedFlavor} onValueChange={v => setSelectedFlavor(v as 'all' | ConnectionFlavor)}>
          <SelectTrigger aria-label="Filter MCP connections by flavor" className="w-44">
            <SelectValue placeholder="All Flavors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Flavors</SelectItem>
            <SelectItem value="marketplace">From Marketplace</SelectItem>
            <SelectItem value="external">External</SelectItem>
          </SelectContent>
        </Select>
        {canRegisterMcp && (
          <Button size="sm" className="gap-1.5" onClick={() => navigate({ to: '/connections/new-mcp' })}>
            <Server className="h-3.5 w-3.5" /> Register Server
          </Button>
        )}
      </div>

      {/* Tier sections */}
      {grouped.map(({ tier, servers: tierServers }) => {
        const TierIcon = TIER_ICONS[tier];
        const info = TIER_LABELS[tier];

        return (
          <div key={tier} className="space-y-3">
            {/* Tier header */}
            <div className="flex items-center gap-2">
              <TierIcon className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold">{info.label}</h3>
              <Badge variant="secondary" className="text-[9px]">{tierServers.length}</Badge>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">{info.description}</p>

            {tierServers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {tierServers.map(server => (
                  <Card
                    key={server.id}
                    className="hover:border-primary/30 transition-colors group"
                  >
                    <CardContent className="pt-4 pb-3 space-y-3">
                      {/* Header: status + name + flavor + tier */}
                      <div className="flex items-start justify-between gap-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <StatusDot status={statusToDot(server.status)} size="sm" />
                          <span className="font-medium text-sm truncate">{server.displayName}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {/* bug #6 — per-card Flavor badge (Marketplace / External). */}
                          <FlavorBadge flavor={serverFlavor(server)} />
                          <Badge variant="outline" className={cn('text-[9px]', TIER_BADGE_STYLES[server.tier])}>
                            {info.label.split(' — ')[0]}
                          </Badge>
                        </div>
                      </div>

                      {/* Namespace + endpoint */}
                      <div className="space-y-0.5">
                        <p className="text-xs font-mono text-muted-foreground">{server.namespace}.*</p>
                        <p className="text-[10px] text-muted-foreground/70 truncate">{server.endpointUrl}</p>
                      </div>

                      {/* bug #2 — connector-status-truth: when the backend reports
                          the connector is added-but-not-usable, surface a real
                          "Sign in to use" / "Setup required" affordance instead of
                          leaving the user with only an amber dot. Renders nothing
                          when the connector is genuinely usable. */}
                      <ConnectionSetupAffordance
                        needsSetupReason={server.needsSetupReason}
                        requiresAuth={server.requiresAuth}
                        signedIn={server.signedIn}
                        onSetup={() => navigate({ to: '/connections/mcp/$connectionId', params: { connectionId: server.id } })}
                        onTest={() => handleTest(server.id)}
                        testing={testServer.isPending && testServer.variables === server.id}
                      />

                      {/* Stats row */}
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div>
                          <p className="text-sm font-semibold">{server.toolCount}</p>
                          <p className="text-[10px] text-muted-foreground">Tools</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{server.metrics.latencyMs != null ? <>{server.metrics.latencyMs}<span className="text-[10px] font-normal text-muted-foreground">ms</span></> : <span className="text-muted-foreground">—</span>}</p>
                          <p className="text-[10px] text-muted-foreground">Latency</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{server.metrics.uptimePercent != null ? `${server.metrics.uptimePercent}%` : <span className="text-muted-foreground">—</span>}</p>
                          <p className="text-[10px] text-muted-foreground">Uptime</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{server.metrics.errorRate != null ? `${(server.metrics.errorRate * 100).toFixed(1)}%` : <span className="text-muted-foreground">—</span>}</p>
                          <p className="text-[10px] text-muted-foreground">Errors</p>
                        </div>
                      </div>

                      <Separator />

                      {/* Bottom: CB badge + actions */}
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className={cn('text-[9px]', CB_BADGE_STYLES[server.circuitBreaker.state])}>
                          CB: {server.circuitBreaker.state.replace('_', ' ')}
                          {server.circuitBreaker.failures > 0 && ` (${server.circuitBreaker.failures}/${server.circuitBreaker.threshold})`}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleTest(server.id)}
                            aria-label={`Test MCP server '${server.displayName}'`}
                            title="Test connection"
                            disabled={testServer.isPending}
                          >
                            <RefreshCw className={cn('h-3.5 w-3.5', testServer.isPending && testServer.variables === server.id && 'animate-spin')} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => setSheetServer(server)}
                            aria-label={`View MCP server '${server.displayName}'`}
                            title="View"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label={`Server actions for '${server.displayName}'`}
                                title="More actions"
                              >
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate({ to: '/connections/mcp/$connectionId', params: { connectionId: server.id } })}>
                                <Settings2 className="h-3.5 w-3.5 mr-2" /> Configure
                              </DropdownMenuItem>
                              {server.circuitBreaker.state !== 'closed' && (
                                <DropdownMenuItem onClick={() => handleResetCB(server.id)}>
                                  <RotateCcw className="h-3.5 w-3.5 mr-2" /> Reset Circuit Breaker
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {/*
                                Codex retest #241 — Tier 1 IQ Native
                                servers are platform-published and
                                cannot be uninstalled by tenant admins.
                                The action used to be available and
                                would 4xx server-side; surfacing it as
                                disabled-with-tooltip both protects the
                                row and educates admins about the
                                policy. The visible-but-disabled
                                pattern is preferred over hiding so
                                power users still know the action
                                exists at the platform layer.
                              */}
                              {server.tier === 'tier1' ? (
                                <DropdownMenuItem
                                  disabled
                                  aria-disabled="true"
                                  data-testid="mcp-remove-tier1-disabled"
                                  data-tier="tier1"
                                  className="text-muted-foreground opacity-50 cursor-not-allowed"
                                  title="System-managed — cannot remove. Tier 1 IQ Native servers are managed by the platform."
                                  onClick={(e) => e.stopPropagation()}
                                  onSelect={(e) => e.preventDefault()}
                                >
                                  <Lock className="h-3.5 w-3.5 mr-2" /> Remove (system-managed)
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  data-testid="mcp-remove"
                                  data-tier={server.tier}
                                  className="text-destructive"
                                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(server); }}
                                >
                                  <Trash2 className="h-3.5 w-3.5 mr-2" /> Remove
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-center">
                <Plug className="h-8 w-8 text-muted-foreground/30 mb-2" />
                <p className="text-sm font-medium text-muted-foreground">No servers registered</p>
                <p className="text-xs text-muted-foreground/70 mt-1 mb-3">
                  {tier === 'tier1' ? 'IQ Native servers are provisioned during installation' :
                   tier === 'tier2' ? 'No Tier 2 connectors registered for this tenant. Click "Register Server" to connect an external enterprise system.' :
                   'Generate MCP servers automatically from API specifications'}
                </p>
                {canRegisterMcp && (
                  <Button variant="outline" size="sm" onClick={() => navigate({ to: '/connections/new-mcp' })}>
                    Register Server
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Empty state when all filtered out */}
      {filtered.length === 0 && search && (
        <div className="text-center py-12">
          <Search className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No servers matching "{search}"</p>
        </div>
      )}

      {/* Tool Catalog Sheet */}
      <Sheet open={!!sheetServer} onOpenChange={open => { if (!open) setSheetServer(null); }}>
        <SheetContent className="w-[480px] sm:max-w-[480px]">
          {sheetServer && (
            <div className="space-y-4">
              <SheetHeader>
                <div className="flex items-center gap-2">
                  <StatusDot status={statusToDot(sheetServer.status)} size="sm" />
                  <SheetTitle>{sheetServer.displayName}</SheetTitle>
                </div>
                {/* Codex retest #243 — Radix complains when a Dialog/Sheet
                    has no Description / aria-describedby. Use the existing
                    tier+namespace caption as the accessible description so
                    screen readers get the context the visual reader sees. */}
                <SheetDescription className="text-xs text-muted-foreground">
                  {TIER_LABELS[sheetServer.tier].label} · {sheetServer.namespace}.*
                </SheetDescription>
              </SheetHeader>

              {/* Stat cards */}
              <div className="grid grid-cols-3 gap-3">
                <Card>
                  <CardContent className="pt-3 pb-2 text-center">
                    <p className="text-lg font-bold">{sheetServer.toolCount}</p>
                    <p className="text-[10px] text-muted-foreground">Tools</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-3 pb-2 text-center">
                    <Badge variant="outline" className={cn('text-[9px]', STATUS_BADGE_STYLES[sheetServer.status])}>
                      {sheetServer.status}
                    </Badge>
                    <p className="text-[10px] text-muted-foreground mt-1">Status</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-3 pb-2 text-center">
                    <p className="text-lg font-bold">{sheetServer.circuitBreaker.failures}</p>
                    <p className="text-[10px] text-muted-foreground">Failures</p>
                  </CardContent>
                </Card>
              </div>

              <Separator />

              {/* Tool list */}
              <div>
                <h4 className="text-sm font-semibold mb-2">Tool Catalog</h4>
                <div className="space-y-2">
                  {sheetServer.tools.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      No tool catalog metadata available for this server yet.
                    </p>
                  )}
                  {sheetServer.tools.map((tool, idx) => {
                    const isWrite = tool.isWriteOperation || tool.permissionLevel === 'write';
                    const permLabel =
                      PERMISSION_LABELS[tool.permissionLevel] ?? PERMISSION_LABELS.auto_approve;
                    return (
                      <div
                        key={tool.name ?? `tool-${idx}`}
                        className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-mono text-xs font-medium">
                            {tool.name ?? '(unnamed tool)'}
                          </p>
                          <div className="flex items-center gap-1.5">
                            {isWrite ? (
                              <Badge variant="outline" className="text-[9px] bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20">
                                <Lock className="h-2.5 w-2.5 mr-0.5" /> Write
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
                                <Unlock className="h-2.5 w-2.5 mr-0.5" /> Read
                              </Badge>
                            )}
                            <Badge variant="outline" className={cn('text-[9px]', permLabel.className)}>
                              {permLabel.label}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground">{tool.description ?? ''}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground/70">
                          <span>{tool.callCount != null ? `${tool.callCount.toLocaleString()} calls` : '—'}</span>
                          <span>{tool.avgLatencyMs != null ? `~${tool.avgLatencyMs}ms avg` : '—'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove MCP Server</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{deleteTarget?.displayName}</strong>?
              This will disconnect all {deleteTarget?.toolCount} tools provided by this server.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Remove Server</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
